import { inject, injectable } from 'inversify';
import { CLIENT_EVENTS } from '../constants/events';
import EnhancedEventEmitter from '../EnhancedEventEmitter';
import { TOKEN } from '../inversify.tokens';
import {
  PeerGroup,
  PeerResponse,
  ProducerData,
  Role,
  TrackLabel,
} from '../types/common';
import type { PeerFactory } from '../types/container';
import { MyPeer } from './MyPeer';
import Network from './network';
import Peer from './Peer';
import PeerConsumer from './PeerConsumer';
import PeersStorage from './PeersStorage';

export interface IPeersService {
  has: (id: Peer['id']) => boolean;
  get: (id: Peer['id']) => Peer | undefined;
  remove: (id: Peer['id']) => Promise<void>;
  all: () => Peer[];
  hosts: () => Peer[];
  audience: () => Peer[];
  reset: () => Promise<void>;
  load: (role?: Role) => Promise<void>;
  add: (peerData: PeerResponse, emit: boolean) => Peer;
  isModerator: (id: Peer['id'] | undefined) => boolean;
}

export interface CreatePeerParams {
  id: string;
  channelIds: string[];
  groups: PeerGroup[];
  producePermissions: TrackLabel[];
  appId: string;
  producers: ProducerData[];
  videoConsumer?: PeerConsumer;
  audioConsumer?: PeerConsumer;
  soundLevel?: number;
  loginDate: Date;
  uid?: string;
  appData?: Record<string, unknown>;
  role: Role;
}

@injectable()
export class Peers implements IPeersService {
  constructor(
    @inject(TOKEN.MyPeer) private readonly myPeer: MyPeer,
    @inject(TOKEN.Network) private readonly network: Network,
    @inject(TOKEN.PeerFactory) private readonly peerFactory: PeerFactory,
    @inject(TOKEN.ClientEventEmitter) private readonly clientEventEmitter: EnhancedEventEmitter,
    @inject(TOKEN.PeersStorage) private readonly peersStorage: PeersStorage,
  ) { }

  has(id: Peer['id']): boolean {
    return this.peersStorage.has(id);
  }

  private set(id: Peer['id'], peer: Peer): void {
    if (id === this.myPeer.id) {
      this.myPeer.set(peer);
    }

    this.peersStorage.set(id, peer);
  }

  get(id: Peer['id']): Peer | undefined {
    return this.peersStorage.get(id);
  }

  private delete(id: Peer['id']): boolean {
    if (id === this.myPeer.id) {
      this.myPeer.delete();
    }

    return this.peersStorage.delete(id);
  }

  async remove(id: Peer['id']): Promise<void> {
    const peer = this.get(id);

    if (!peer) {
      return;
    }

    await peer.reset();

    this.delete(id);
    this.clientEventEmitter.safeEmit(CLIENT_EVENTS.peerLeft, id);
  }

  all(): Peer[] {
    return this.peersStorage.all();
  }

  hosts(): Peer[] {
    return this.peersStorage.hosts();
  }

  audience(): Peer[] {
    return this.peersStorage.audience();
  }

  async reset(): Promise<void> {
    await Promise.all(this.peersStorage.all().map(async (peer) => await peer.reset()));
    this.peersStorage.clear();
  }

  async load(role?: Role): Promise<void> {
    const { peers } = await this.network.getChannelPeers(role);
    peers
      .filter((peer) => !this.has(peer.id))
      .forEach((peer) => this.add(peer, false));
  }

  add(peerData: PeerResponse, emit = true): Peer {
    const existingPeer = this.get(peerData.id);

    if (existingPeer) {
      return existingPeer;
    }

    const peer = this.peerFactory(peerData);

    this.set(peer.id, peer);

    if (emit) {
      this.clientEventEmitter.safeEmit(CLIENT_EVENTS.peerJoined, peer);
    }

    return peer;
  }

  isModerator(id: Peer['id'] | undefined): boolean {
    if (id === undefined) {
      return false;
    }

    const peer = this.get(id);

    if (peer) {
      return peer.isModerator;
    }

    return false;
  }
}
